import { CHARACTER_SLOTS, type CharacterShape, type CharacterSlotId } from '@momentum/shared';

/**
 * Small helpers shared by `board3d.tsx`. Colour comes from the CSS custom property named by
 * `CHARACTER_SLOTS[slot].colorToken` (see docs/ASSETS.md), read live via `getComputedStyle` so a theme swap
 * takes effect without touching this file; a hard-coded fallback keeps the scene legible before `theme.css`
 * defines the token (or in non-DOM test environments).
 */
const FALLBACK_HEX: Record<CharacterShape, string> = {
  square: '#c9a6f5',
  oval: '#1e7cf0',
  triangle: '#ff7a6b',
  hexagon: '#ffcc33',
  diamond: '#4cc38a',
  circle: '#2b2d33',
};

const NEUTRAL_SHAPE: CharacterShape = 'circle';
const NEUTRAL_TOKEN = 'char-charcoal';

export function readColorToken(token: string, fallbackHex: string): string {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallbackHex;
  const value = getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
  return value.length > 0 ? value : fallbackHex;
}

export function characterShape(slot: CharacterSlotId | null): CharacterShape {
  return slot ? CHARACTER_SLOTS[slot].shape : NEUTRAL_SHAPE;
}

/** Resolves a character slot (or `null` for an unassigned/neutral token) to a CSS colour string. */
export function characterColor(slot: CharacterSlotId | null): string {
  const shape = characterShape(slot);
  const token = slot ? CHARACTER_SLOTS[slot].colorToken : NEUTRAL_TOKEN;
  return readColorToken(token, FALLBACK_HEX[shape]);
}

export function readTheme(token: string, fallbackHex: string): string {
  return readColorToken(token, fallbackHex);
}
